import { useEffect } from 'react'
import { App as AntdApp, Checkbox, Form, Input, Modal, Select } from 'antd'
import {
  KNOWLEDGE_KIND_OPTIONS,
  KNOWLEDGE_PROBLEM_TYPE_OPTIONS,
  toProductSelectOptions,
} from '@/modules/knowledge/constants'
import { useCreateKnowledge, useUpdateKnowledge } from '@/modules/knowledge/queries'
import type {
  KnowledgeCreatePayload,
  KnowledgeEntry,
  KnowledgeKind,
  KnowledgeProblemType,
  KnowledgeProductOption,
  KnowledgeUpdatePayload,
} from '@/types/knowledge'

interface KnowledgeFormValues {
  title: string
  content: string
  kind: KnowledgeKind
  product_key: string
  // 空串是后端的合法取值（未分类），见下面的 PROBLEM_TYPE_OPTIONS
  problem_type: KnowledgeProblemType | ''
  keywords: string[]
  source_url?: string
  publish?: boolean
}

/**
 * 问题类型选项。「未分类」（空串）必须可选：聚合条目可能没落到 11 类里的任何一类 ——
 * 这是 ``KnowledgeEntry.problem_type`` 明确建模的状态（"" = 未分类），审核信号
 * ``unclassified_problem`` 判的就是它，列表与详情也都显示"未分类"。
 * 表单若把它当非法值，审核者改一个错别字也会被"请输入问题类型"拦住。
 */
const PROBLEM_TYPE_OPTIONS = [
  { value: '', label: '未分类' },
  ...KNOWLEDGE_PROBLEM_TYPE_OPTIONS,
]

interface Props {
  open: boolean
  // null 表示新增
  entry: KnowledgeEntry | null
  productOptions: KnowledgeProductOption[]
  // 只有 manager/admin 可以勾选"直接发布"
  canPublishDirectly: boolean
  onClose: () => void
}

const DEFAULT_VALUES: Partial<KnowledgeFormValues> = {
  kind: 'issue',
  // 空串即"未归类"，是表单允许的合法取值
  product_key: '',
  problem_type: 'other',
  keywords: [],
  publish: false,
}

/**
 * 条目 → 表单值。**所有键都显式给出**：漏掉的键不会被清空，
 * 会把上一条的残留值留在表单里（见下面 useEffect 的说明）。
 */
function entryFormValues(entry: KnowledgeEntry): Partial<KnowledgeFormValues> {
  return {
    title: entry.title,
    content: entry.content,
    kind: entry.kind,
    product_key: entry.product_key,
    problem_type: entry.problem_type,
    keywords: entry.keywords || [],
    source_url: entry.source_url || undefined,
  }
}

export default function KnowledgeEditModal({
  open,
  entry,
  productOptions,
  canPublishDirectly,
  onClose,
}: Props) {
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm<KnowledgeFormValues>()
  const createMutation = useCreateKnowledge()
  const updateMutation = useUpdateKnowledge()
  const isEdit = !!entry
  const initialValues: Partial<KnowledgeFormValues> = entry
    ? entryFormValues(entry)
    : DEFAULT_VALUES

  // 表单实例比弹窗活得久：Modal 关闭会销毁子节点，但 Form.useForm() 的 store 留在本组件里，
  // 而 rc-field-form 的 setInitialValues 是"旧值优先于 initialValues" —— 只靠 initialValues，
  // 第二次打开带出的会是**上一条**的标题与正文（业务方反馈"点编辑显示的是历史的"）。
  // 因此每次打开都显式同步成当前条目的值（顺带清掉上一次的校验错误）。
  useEffect(() => {
    if (!open) return
    form.resetFields()
    form.setFieldsValue(entry ? entryFormValues(entry) : DEFAULT_VALUES)
  }, [open, entry, form])

  const handleFinish = (values: KnowledgeFormValues) => {
    const payload: KnowledgeCreatePayload = {
      title: values.title.trim(),
      content: values.content,
      kind: values.kind,
      product_key: values.product_key,
      // 新增必须落到一个具体分类：后端枚举不接受空串，表单也在新增时设为必填。
      // 兜底用 other 与后端 ``CreateKnowledgeEntryRequest`` 的默认值同一口径。
      problem_type: values.problem_type || 'other',
      keywords: values.keywords || [],
      source_url: values.source_url?.trim() || undefined,
      publish: canPublishDirectly && !!values.publish,
    }
    const onSuccess = () => {
      message.success(isEdit ? '保存成功' : payload.publish ? '新增并发布成功' : '新增成功，可在草稿中继续完善')
      onClose()
    }
    const onError = (err: unknown) =>
      message.error(err instanceof Error ? err.message : isEdit ? '保存失败' : '新增失败')

    if (entry) {
      const update: KnowledgeUpdatePayload = {
        title: payload.title,
        content: payload.content,
        kind: payload.kind,
        product_key: payload.product_key,
        keywords: payload.keywords,
        source_url: payload.source_url,
      }
      // 「未分类」不在后端的枚举里，回传空串会被 422 拒掉；不传该字段 = 保持原状。
      // 因此这里只发非空取值 —— 想让已分类的条目退回"未分类"需要后端放开枚举，另议。
      if (values.problem_type) update.problem_type = values.problem_type
      updateMutation.mutate({ entryId: entry.id, payload: update }, { onSuccess, onError })
      return
    }
    createMutation.mutate(payload, { onSuccess, onError })
  }

  return (
    <Modal
      title={isEdit ? '编辑知识条目' : '新增知识条目'}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="保存"
      cancelText="取消"
      confirmLoading={createMutation.isPending || updateMutation.isPending}
      destroyOnHidden
      width={720}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={initialValues}>
        <Form.Item label="标题" name="title" rules={[{ required: true, whitespace: true, message: '请填写标题' }, { max: 255 }]}>
          <Input placeholder="例如：报到机「未在班」提示排查方法" />
        </Form.Item>
        <Form.Item label="正文" name="content" rules={[{ required: true, whitespace: true, message: '请填写正文' }]}>
          <Input.TextArea rows={8} placeholder="填写现象、原因与处理步骤，支持多行" />
        </Form.Item>
        <Form.Item label="知识形态" name="kind" rules={[{ required: true }]}>
          <Select options={KNOWLEDGE_KIND_OPTIONS} />
        </Form.Item>
        <Form.Item
          label="产品"
          name="product_key"
          extra="产品分类以产品彩页为准；选择「未归类」表示该知识不归属任何具体彩页产品"
        >
          <Select options={toProductSelectOptions(productOptions)} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item
          label="问题类型"
          name="problem_type"
          // 编辑时「未分类」是合法取值（聚合条目可能没落到任何一类），新增时必须有分类
          rules={isEdit ? [] : [{ required: true, message: '请选择问题类型' }]}
          extra={isEdit ? '「未分类」可原样保留，不会阻断保存' : undefined}
        >
          <Select options={PROBLEM_TYPE_OPTIONS} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item label="关键词" name="keywords" extra="回车确认，可填多个">
          <Select mode="tags" open={false} placeholder="输入关键词后回车" tokenSeparators={[',', '，']} />
        </Form.Item>
        <Form.Item label="来源链接" name="source_url" rules={[{ max: 512 }]}>
          <Input placeholder="选填，禅道任务或其他外部来源地址" />
        </Form.Item>
        {canPublishDirectly && !isEdit && (
          <Form.Item
            label="直接发布"
            name="publish"
            valuePropName="checked"
            extra="不勾选则保存为草稿"
          >
            <Checkbox>立即发布</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
